import { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, Alert, Chip, Divider,
} from '@mui/material';
import { CheckCircleOutlined, NotificationsActiveOutlined, NotificationsOffOutlined } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { usePush } from '../hooks/usePush';

export default function Settings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [linkCode, setLinkCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const push = usePush();

  const nameMutation = useMutation({
    mutationFn: () => api.patch('/api/users/me', { name }),
    onSuccess: () => refresh(),
  });

  const codeMutation = useMutation({
    mutationFn: () => api.post<{ code: string; expiresAt: string }>('/api/users/telegram/link-code'),
    onSuccess: (data) => setLinkCode(data),
  });

  const unlinkMutation = useMutation({
    mutationFn: () => api.post('/api/users/telegram/unlink'),
    onSuccess: () => {
      setLinkCode(null);
      refresh();
    },
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>Settings</Typography>

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Your profile</Typography>
          <Stack spacing={2}>
            <TextField
              label="Email"
              value={user?.email ?? ''}
              disabled
              size="small"
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
            />
            {nameMutation.isError && <Alert severity="error">{(nameMutation.error as Error).message}</Alert>}
            {nameMutation.isSuccess && <Alert severity="success">Saved.</Alert>}
            <Box>
              <Button
                variant="contained"
                onClick={() => nameMutation.mutate()}
                disabled={nameMutation.isPending || name === user?.name || !name.trim()}
              >
                {nameMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Notifications — web push on this device */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notifications on this device</Typography>
            {push.subscribed && (
              <Chip
                icon={<NotificationsActiveOutlined />}
                label="On"
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            {push.permission === 'denied' && (
              <Chip
                icon={<NotificationsOffOutlined />}
                label="Blocked"
                color="error"
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Get OS-level pop-ups on this device when reminders fire, meds are given, or handovers are logged —
            even when the care app isn't open. On iPhone/iPad you need to <b>add care.marwood.au to your home screen</b>
            (Share sheet → Add to Home Screen) first; Safari only allows push for installed PWAs.
          </Typography>

          {push.permission === 'unsupported' && (
            <Alert severity="info">
              Your browser doesn't support web push. Try Chrome, Edge, Firefox, or Safari 16.4+.
            </Alert>
          )}

          {push.permission === 'denied' && (
            <Alert severity="warning">
              Notifications are blocked for this site. Re-enable them in your browser or OS settings, then reload this page.
            </Alert>
          )}

          {push.permission !== 'unsupported' && push.permission !== 'denied' && (
            <Stack direction="row" spacing={1.5}>
              {push.subscribed ? (
                <>
                  <Button
                    onClick={push.sendTest}
                    startIcon={<NotificationsActiveOutlined />}
                    variant="outlined"
                  >
                    Send test
                  </Button>
                  <Button
                    onClick={push.disable}
                    disabled={push.loading}
                    color="error"
                    variant="outlined"
                  >
                    {push.loading ? 'Turning off…' : 'Turn off on this device'}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={push.enable}
                  disabled={push.loading || !push.vapidConfigured}
                  variant="contained"
                  startIcon={<NotificationsActiveOutlined />}
                >
                  {push.loading ? 'Enabling…' : 'Enable on this device'}
                </Button>
              )}
            </Stack>
          )}

          {push.error && <Alert severity="error" sx={{ mt: 2 }}>{push.error}</Alert>}
          {!push.vapidConfigured && push.permission !== 'unsupported' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Web push isn't configured on the server yet. Ask an admin to set <code>VAPID_PUBLIC_KEY</code>.
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Telegram</Typography>
            {user?.telegramLinked && (
              <Chip
                icon={<CheckCircleOutlined />}
                label="Linked"
                color="success"
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Log entries from the "Mum's Care" group chat (via <b>@Marwood_X_HerdBot</b> — already in the group).
            Send <code>/med</code>, <code>/symptom</code>, <code>/note</code>, <code>/handover</code>, <code>/meal</code>,
            or <code>/appointment</code> followed by the details. New entries + reminder acks flow both ways.
          </Typography>

          {user?.telegramLinked ? (
            <Stack spacing={2}>
              <Alert severity="success">
                Your Telegram account is linked. Anything you post in the "Mum's Care" group
                chat starting with a command is attributed to you.
              </Alert>
              <Box>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => unlinkMutation.mutate()}
                  disabled={unlinkMutation.isPending}
                >
                  Unlink Telegram
                </Button>
              </Box>
            </Stack>
          ) : linkCode ? (
            <Stack spacing={2}>
              <Alert severity="info">
                In the <b>Mum's Care</b> group chat, send:{' '}
                <Box component="code" sx={{
                  display: 'inline-block',
                  px: 1,
                  py: 0.25,
                  bgcolor: 'action.selected',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                }}>
                  /start {linkCode.code}
                </Box>
                <br />
                Expires {new Date(linkCode.expiresAt).toLocaleTimeString()}.
              </Alert>
              <Box>
                <Button onClick={() => codeMutation.mutate()} disabled={codeMutation.isPending}>
                  Generate a new code
                </Button>
              </Box>
            </Stack>
          ) : (
            <Box>
              <Button
                variant="contained"
                onClick={() => codeMutation.mutate()}
                disabled={codeMutation.isPending}
              >
                {codeMutation.isPending ? 'Generating…' : 'Generate link code'}
              </Button>
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Privacy</Typography>
          <Typography variant="body2" color="text.secondary">
            This app is private. Only people whose email is on the server allow-list can request
            a sign-in link. There are no public pages.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Role: <strong>{user?.role}</strong>
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
}
